import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useNavigate } from 'react-router-dom';
import { getMyDirectReports } from '../utils/api';
import {
  ArrowLeftIcon,
  UserGroupIcon,
  EnvelopeIcon,
  CakeIcon,
} from '@heroicons/react/24/outline';

const DirectReports = () => {
  usePageTitle('Direct Reports');

  const navigate = useNavigate();
  const [directReports, setDirectReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDirectReports();
  }, []);

  const fetchDirectReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getMyDirectReports();
      setDirectReports(response.data.direct_reports);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No employee profile is linked to your account.');
      } else {
        setError('Failed to load direct reports. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner" />
          <span>Loading direct reports...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-ghost" onClick={() => navigate(-1)}>
            <ArrowLeftIcon style={{ width: 20, height: 20, marginRight: 8 }} />
            Back
          </button>
        </div>
        <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
          <UserGroupIcon style={{ width: 48, height: 48, color: 'var(--color-text-muted)', margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <button className="btn btn-ghost" onClick={() => navigate(-1)}>
          <ArrowLeftIcon style={{ width: 20, height: 20, marginRight: 8 }} />
          Back
        </button>
        <h1>Direct Reports</h1>
      </div>

      {directReports.length === 0 ? (
        <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
          <UserGroupIcon style={{ width: 48, height: 48, color: 'var(--color-text-muted)', margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            You don't have any direct reports.
          </p>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '16px', color: 'var(--color-text-secondary)' }}>
            {directReports.length} employee{directReports.length !== 1 ? 's' : ''} reporting to you
          </div>
          <div className="card">
            <table className="table">
              <thead>
                <tr>
                  <th>Employee ID</th>
                  <th>Name</th>
                  <th>Date of Birth</th>
                  <th>Position</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {directReports.map((employee) => (
                  <tr key={employee.id}>
                    <td style={{ fontFamily: 'monospace', color: 'var(--color-text-muted)' }}>
                      {employee.employee_id}
                    </td>
                    <td style={{ fontWeight: 500 }}>
                      {employee.first_name} {employee.last_name}
                    </td>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <CakeIcon style={{ width: 14, height: 14, color: 'var(--color-text-muted)' }} />
                        {formatDate(employee.date_of_birth)}
                      </span>
                    </td>
                    <td>{employee.position || '-'}</td>
                    <td>
                      <a
                        href={`mailto:${employee.company_email}`}
                        style={{ color: 'var(--color-primary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
                      >
                        <EnvelopeIcon style={{ width: 14, height: 14 }} />
                        {employee.company_email}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default DirectReports;
